/*
  AXORY US storefront behaviour: Year/Make/Model finder, tabs, product gallery,
  quantity stepper, "fits your vehicle" check and the makes toggle.
  Loaded only for the United States market (layout/theme.liquid).
*/
(() => {
  if (window.axusLoaded) return;
  window.axusLoaded = true;

  const STORE_KEY = 'axus:vehicle';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let dataPromise;

  const loadData = (src) => {
    dataPromise =
      dataPromise ||
      fetch(src).then((r) => {
        if (!r.ok) throw new Error(`fitment data ${r.status}`);
        return r.json();
      });
    return dataPromise;
  };

  const readVehicle = () => {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY)) || null;
    } catch (e) {
      return null;
    }
  };

  const saveVehicle = (v) => {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(v));
    } catch (e) {
      /* storage unavailable: finder still works for this page view */
    }
  };

  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const years = (y1, y2) => (y1 === y2 ? `${y1}` : `${y1}–${y2}`);
  const productUrl = (handle) => `${root}products/${handle}`;
  const byText = (a, b) => a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });

  /* Finder analytics: outcome only, never the registration (GA4 + Shopify customer events). */
  const track = (name, params) => {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
      const a = window.Shopify && window.Shopify.analytics;
      if (a && typeof a.publish === 'function') a.publish(name, params);
    } catch (e) {
      /* analytics must never break the finder */
    }
  };

  /* ---------------- Year / Make / Model ---------------- */
  /* Models listed for fitment but never sold in the US under that name: shown last, in their own group. */
  const NON_US = {
    'BMW': ['iX3'],
    'Chevrolet': ['D-Max'],
    'Ford': ['Fiesta Active', 'Galaxy', 'Grand C-Max', 'Kuga', 'Mondeo', 'Puma', 'Transit Courier'],
    'Hyundai': ['Bayon', 'Matrix', 'Trajet', 'i20 Active', 'i30', 'ix35'],
    'Kia': ['Carens', 'Ceed', 'EV3', 'EV5', 'Grand Carnival', 'XCeed'],
    'Mercedes-Benz': ['Citan', 'Vaneo'],
    'Mitsubishi': ['ASX', 'L200', 'Pajero', 'Pajero Sport'],
    'Nissan': ['Navara', 'Primera', 'Qashqai', 'X-Trail'],
    'Subaru': ['XV'],
    'Toyota': ['Auris', 'Corolla', 'Corolla Spacio', 'Corolla Verso', 'Fortuner', 'Hilux', 'Proace City Verso', 'Rush', 'Verso', 'Yaris Cross'],
    'Volkswagen': ['Amarok', 'Caddy', 'Golf Sportvan', 'Nivus', 'Sharan', 'T-Cross', 'T-Roc', 'Taigo', 'Tayron', 'Teramont', 'Touran'],
    'Volvo': ['V40 Cross Country'],
  };
  const NOT_LISTED = '__not_listed';
  class AxusYmm extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector('form');
      this.year = this.form.elements.year;
      this.make = this.form.elements.make;
      this.model = this.form.elements.model;
      this.button = this.form.querySelector('[type="submit"]');
      this.results = this.querySelector('[data-results]');

      const warm = () => this.ready().catch(() => {});
      ['pointerdown', 'focusin', 'touchstart'].forEach((type) =>
        this.addEventListener(type, warm, { once: true, passive: true }),
      );
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
      if (document.readyState === 'complete') idle(warm);
      else window.addEventListener('load', () => idle(warm), { once: true });

      this.year.addEventListener('change', () => this.onYear());
      this.make.addEventListener('change', () => {
        if (this.make.value === NOT_LISTED) return this.notListed();
        this.onMake();
      });
      this.model.addEventListener('change', () => {
        if (this.model.value === NOT_LISTED) return this.notListed();
        this.sync();
        if (this.model.value) this.show();
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.show();
      });

      const saved = readVehicle();
      if (saved) this.ready().then(() => this.restore(saved)).catch(() => {});
    }

    ready() {
      this.readyPromise =
        this.readyPromise ||
        loadData(this.dataset.src)
          .then((data) => {
            this.data = data;
            this.fillYears();
            return data;
          })
          .catch((error) => {
            this.fail();
            throw error;
          });
      return this.readyPromise;
    }

    fillYears() {
      const current = this.year.value;
      let min = Infinity;
      let max = -Infinity;
      this.data.fits.forEach((f) => {
        min = Math.min(min, f[3]);
        max = Math.max(max, f[4]);
      });
      const list = [];
      for (let y = max; y >= min; y -= 1) {
        if (this.data.fits.some((f) => f[3] <= y && y <= f[4])) list.push(String(y));
      }
      this.fill(this.year, list, this.year.options[0].textContent);
      if (list.includes(current)) this.year.value = current;
    }

    fits(year, make) {
      return this.data.fits.filter(
        (f) => f[3] <= year && year <= f[4] && (make === undefined || this.data.makes[f[0]] === make),
      );
    }

    fill(select, values, placeholder) {
      select.querySelectorAll('optgroup').forEach((g) => g.remove());
      select.length = 1;
      select.options[0].textContent = placeholder;
      values.forEach((v) => select.add(new Option(v, v)));
      select.disabled = values.length === 0;
    }

    async onYear() {
      await this.ready();
      const year = Number(this.year.value);
      const makes = year ? [...new Set(this.fits(year).map((f) => this.data.makes[f[0]]))].sort(byText) : [];
      this.fill(this.make, makes, 'Select make');
      if (year) this.make.add(new Option("My make isn't listed", NOT_LISTED));
      this.fill(this.model, [], 'Select model');
      this.sync();
    }

    onMake() {
      const year = Number(this.year.value);
      const make = this.make.value;
      const models = make ? [...new Set(this.fits(year, make).map((f) => f[1]))].sort(byText) : [];
      const other = NON_US[make] || [];
      this.fill(this.model, models.filter((m) => !other.includes(m)), 'Select model');
      const abroad = models.filter((m) => other.includes(m));
      if (abroad.length) {
        const group = document.createElement('optgroup');
        group.label = 'Models sold outside the US';
        abroad.forEach((m) => group.appendChild(new Option(m, m)));
        this.model.appendChild(group);
      }
      if (make) {
        this.model.add(new Option("My model isn't listed", NOT_LISTED));
        this.model.disabled = false;
      }
      this.sync();
    }

    sync() {
      this.button.disabled = !(this.year.value && this.make.value && this.model.value);
      this.results.hidden = true;
    }

    restore(v) {
      if (![...this.year.options].some((o) => o.value === String(v.year))) return;
      this.year.value = String(v.year);
      const makes = [...new Set(this.fits(v.year).map((f) => this.data.makes[f[0]]))].sort(byText);
      this.fill(this.make, makes, 'Select make');
      if (!makes.includes(v.make)) return this.sync();
      this.make.value = v.make;
      this.onMake();
      if ([...this.model.options].some((o) => o.value === v.model)) this.model.value = v.model;
      this.sync();
    }

    async show() {
      try {
        await this.ready();
      } catch (e) {
        return;
      }
      const year = Number(this.year.value);
      const make = this.make.value;
      const model = this.model.value;
      if (!(year && make && model)) return;

      const rows = this.fits(year, make).filter((f) => f[1] === model);
      track('axus_ymm_lookup', { outcome: rows.length ? 'match' : 'no_fit', year, make, model });
      saveVehicle({ year, make, model });
      document.dispatchEvent(new CustomEvent('axus:vehicle', { detail: { year, make, model } }));

      this.results.hidden = false;
      if (!rows.length) {
        this.results.innerHTML = `<p class="axus-ymm__msg">We don't list a fit for the ${esc(year)} ${esc(make)} ${esc(model)} yet. <a href="${root}collections/roof-rack-cross-bars">Browse all roof bars</a> or contact us with your vehicle details.</p>`;
        return;
      }

      const label = rows.length === 1 ? '1 fit' : `${rows.length} fits`;
      this.results.innerHTML =
        `<p class="axus-ymm__count">${label} for your ${esc(year)} ${esc(make)} ${esc(model)}${rows.length > 1 ? ' — pick your generation' : ''}</p>` +
        rows.map((r) => this.card(r, make)).join('');
      this.prices();
    }

    card(r, make) {
      const rail = this.data.lines[r[5]] || '';
      const chip = (handle, cls, name) =>
        handle
          ? `<a class="axus-chip" href="${productUrl(handle)}"><span class="axus-dot axus-dot--${cls}" aria-hidden="true"></span>${name}</a>`
          : '';
      return `<article class="axus-fit">
        <div>
          <span class="axus-fit__tag">${esc(rail)}</span>
          <h3 class="axus-fit__name">${esc(make)} ${esc(r[2])}</h3>
          <p class="axus-fit__meta">Model years ${years(r[3], r[4])} · for ${esc(rail.toLowerCase().replace(' rail', ''))} roof rails · set of 2 lockable aluminum bars</p>
        </div>
        <div class="axus-fit__buy">
          <span class="axus-fit__price" data-handle="${esc(r[6] || r[7])}"></span>
          ${chip(r[6], 'black', 'Black')}${chip(r[7], 'silver', 'Silver')}
        </div>
      </article>`;
    }

    prices() {
      this.results.querySelectorAll('[data-handle]').forEach((el) => {
        productData(el.dataset.handle).then((p) => {
          const v = p && p.variants[0];
          if (!v) return el.remove();
          if (!p.available) {
            el.textContent = 'Sold out';
            return;
          }
          el.innerHTML = p.discount_percent
            ? `<span class="axus-p__now">${esc(v.final_price_formatted)}</span> <s class="axus-p__was">${esc(v.price_formatted)}</s>`
            : esc(v.price_formatted);
        });
      });
    }

    notListed() {
      const year = this.year.value;
      const make = this.make.value === NOT_LISTED ? '' : this.make.value;
      track('axus_ymm_lookup', { outcome: 'not_listed', year: Number(year) || null, make: make || 'other' });
      this.button.disabled = true;
      this.results.hidden = false;
      const phone = this.dataset.phone || '';
      const tel = phone.replace(/[^+\d]/g, '');
      this.results.innerHTML = `<div class="axus-ask">
          <p class="axus-ask__lead"><strong>Not on the list yet?</strong> Tell us your vehicle and we'll check whether a set fits, usually within one working day.${
            phone ? ` Or call <a href="tel:${tel}">${esc(phone)}</a>.` : ''
          }</p>
        </div>`;
      const tpl = this.querySelector('template[data-axus-ask]');
      if (!tpl) return;
      const box = this.results.querySelector('.axus-ask');
      box.appendChild(tpl.content.cloneNode(true));
      const form = box.querySelector('form');
      const vehicle = form.querySelector('[data-vehicle]');
      vehicle.value = [year, make].filter(Boolean).join(' ') + (make ? ' ' : '');
      form.addEventListener('submit', () => {
        form.querySelector('[data-body]').value = `Vehicle: ${vehicle.value}`;
        track('axus_ask_submit', { topic: 'US fit check' });
      });
      vehicle.focus();
    }

    fail() {
      this.results.hidden = false;
      this.results.innerHTML = `<p class="axus-ymm__msg">The vehicle finder didn't load. <a href="${root}collections/roof-rack-cross-bars">Browse all roof bars</a> instead.</p>`;
    }
  }

  if (!customElements.get('axus-ymm')) customElements.define('axus-ymm', AxusYmm);

  /* ---------------- Live product data (price, discount, stock) ---------------- */
  const productCache = {};
  const productData = (handle) => {
    productCache[handle] =
      productCache[handle] ||
      fetch(`${productUrl(handle)}?view=axus-json`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    return productCache[handle];
  };

  /* ---------------- Registration lookup (UK) ---------------- */
  const ROOF = {
    raised: { t: 'Raised roof rails', d: 'The rails stand off the roof: you can pass a hand underneath.' },
    flush: { t: 'Flush roof rails', d: 'The rails sit tight against the roof with no gap underneath.' },
    fix: { t: 'Factory fixing points', d: 'No rails, but small covered fixing points along the roof edge.' },
  };
  const ROOF_ORDER = ['raised', 'flush', 'fix'];

  class AxusReg extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector('form');
      this.input = this.form.elements.reg;
      this.status = this.querySelector('[data-status]');
      this.api = (this.dataset.api || '').replace(/\/$/, '');
      this.email = this.dataset.email;
      this.phone = this.dataset.phone || '';
      if (/[?&]contact_posted=true/.test(window.location.search) && window.location.hash === '#find-by-reg') {
        this.status.textContent = "Thanks, we've got your details. We'll email you within one working day.";
      }
      this.input.addEventListener('input', () => {
        this.input.value = this.input.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '').replace(/\s+/g, ' ').slice(0, 9);
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.lookup();
      });
    }

    get stage() {
      return document.getElementById(this.dataset.results);
    }

    lookup() {
      const plate = this.input.value.trim();
      const reg = plate.replace(/\s/g, '');
      if (reg.length < 2) {
        this.input.focus();
        return;
      }
      this.classList.add('is-loading');
      this.status.textContent = 'Checking the DVLA record…';
      fetch(`${this.api}/api/vrm?reg=${encodeURIComponent(reg)}`)
        .then((r) => r.json())
        .then((data) => {
          if (!data || data.ok !== true) throw new Error('bad response');
          this.status.textContent = '';
          try {
            sessionStorage.setItem('aax_vrm', JSON.stringify({ reg, at: Date.now() }));
          } catch (e) {
            /* ignore */
          }
          this.render(data, plate);
        })
        .catch(() => {
          track('axus_reg_lookup', { outcome: 'error' });
          this.status.textContent = `Something went wrong on our side. Please try again, or call us on ${this.phone || this.email}.`;
        })
        .finally(() => this.classList.remove('is-loading'));
    }

    fallback(plate, heading, vehicle) {
      const box = document.createElement('div');
      box.className = 'axus-regfb';
      const tpl = this.querySelector('template[data-axus-ukfit]');
      if (tpl) {
        box.insertAdjacentHTML('beforeend', `<h3 class="axus-regfb__h">${esc(heading)}</h3>`);
        box.appendChild(tpl.content.cloneNode(true));
      }
      box.appendChild(this.ask(`Roof bar check: ${plate}`, { Registration: plate, Vehicle: vehicle || '' }));
      return box;
    }

    ask(subject, fields) {
      const wrap = document.createElement('div');
      wrap.className = 'axus-ask';
      const phone = this.phone
        ? `Call <a href="tel:${this.phone.replace(/[^+\d]/g, '')}">${esc(this.phone)}</a> or l`
        : 'L';
      wrap.innerHTML = `<p class="axus-ask__lead"><strong>Prefer to ask?</strong> ${phone}eave your email and we'll check by hand, usually within one working day.</p>`;
      const tpl = this.querySelector('template[data-axus-ask]');
      if (!tpl) {
        wrap.insertAdjacentHTML('beforeend', `<p><a href="${this.mailto(subject)}">${esc(this.email)}</a></p>`);
        return wrap;
      }
      wrap.appendChild(tpl.content.cloneNode(true));
      const form = wrap.querySelector('form');
      const body = Object.entries(fields || {})
        .filter(([, value]) => value)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n');
      form.querySelector('[data-subject]').value = subject;
      form.querySelector('[data-body]').value = body || subject;
      form.addEventListener('submit', () => track('axus_ask_submit', { topic: subject.split(':')[0] }));
      return wrap;
    }

    mailto(subject) {
      return `mailto:${this.email}?subject=${encodeURIComponent(subject)}`;
    }

    render(data, plate) {
      const stage = this.stage;
      if (!stage) return;
      stage.hidden = false;
      stage.innerHTML = '';
      const all = this.dataset.all;

      if (!data.vehicle) {
        track('axus_reg_lookup', { outcome: 'plate_not_found' });
        stage.innerHTML = `<div class="axus-regv axus-regv--none">
            <span class="axus-regv__plate">${esc(plate)}</span>
            <div><h3>We couldn't find that registration</h3><p>The DVLA has no record of this plate. Check it's typed correctly (0 and O, 1 and I are easy to mix up) and try again, or choose your car below.</p></div>
          </div>`;
        stage.appendChild(this.fallback(plate, 'Choose your car instead'));
        stage.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.input.focus();
        return;
      }

      if (data.status === 'none' || !(data.groups || []).length) {
        track('axus_reg_lookup', { outcome: 'no_fit' });
        const nv = data.vehicle;
        stage.innerHTML = `<div class="axus-regv axus-regv--none">
            <span class="axus-regv__plate">${esc(plate)}</span>
            <div><h3>${esc(nv.label || 'We found your car')}</h3><p>We don't list roof bars for this vehicle yet. Choose your car below to double-check, or ask us and we'll look by hand.</p></div>
          </div>`;
        stage.appendChild(this.fallback(plate, 'Double-check by make and model', nv.label));
        stage.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const v = data.vehicle;
      const live = (data.groups || []).some((g) => g.live);
      track('axus_reg_lookup', { outcome: data.status === 'choose' ? 'choose_roof' : live ? 'match' : 'out_of_stock' });
      const tidy = (x) =>
        String(x)
          .split(/\s+/)
          .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
          .join(' ');
      const facts = [v.year, v.body, v.fuel, v.colour]
        .filter(Boolean)
        .map(tidy)
        .map((x) => `<span>${esc(x)}</span>`)
        .join('');
      const pill =
        data.status === 'choose'
          ? '<span class="axus-pill axus-pill--warn">One question left</span>'
          : live
            ? '<span class="axus-pill axus-pill--ok">Bars available</span>'
            : '<span class="axus-pill axus-pill--warn">Fits, out of stock</span>';
      stage.insertAdjacentHTML(
        'beforeend',
        `<div class="axus-regv"><span class="axus-regv__plate">${esc(plate)}</span>
          <div><h3>${esc(v.label)}</h3><div class="axus-regv__facts">${facts}</div></div>${pill}</div>`,
      );

      if (data.notice === 'plate_in_retention') {
        stage.insertAdjacentHTML(
          'beforeend',
          `<p class="axus-reg__msg axus-reg__msg--warn"><strong>Double-check this one.</strong> This registration has been transferred, so the DVLA record may describe the plate's previous car. If the vehicle above isn't yours, browse <a href="${all}">all roof bars</a> or email <a href="${this.mailto(`Roof bar check — ${plate}`)}">${esc(this.email)}</a>.</p>`,
        );
      }

      if (data.status === 'choose') {
        stage.appendChild(this.chooser(data, plate));
      } else {
        this.paint(data.groups || []);
      }
      stage.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    chooser(data, plate) {
      const box = document.createElement('div');
      box.className = 'axus-regq';
      box.innerHTML =
        '<h3>What does your roof look like?</h3><p>This model was built with more than one roof type and the bars are not interchangeable. Look along the top of your car and pick what you see.</p>';
      const byRoof = {};
      data.groups.forEach((g) => {
        (byRoof[g.roofType] = byRoof[g.roofType] || []).push(g);
      });
      const opts = document.createElement('div');
      opts.className = 'axus-regq__opts';
      ROOF_ORDER.forEach((rt) => {
        if (!byRoof[rt]) return;
        const img = this.dataset[`img${rt.charAt(0).toUpperCase()}${rt.slice(1)}`];
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'axus-regq__opt';
        b.innerHTML = `${img ? `<img src="${img}" alt="" loading="lazy">` : ''}<strong>${ROOF[rt].t}</strong><span>${ROOF[rt].d}</span>`;
        b.addEventListener('click', () => {
          opts.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
          this.stage.querySelectorAll('.axus-regg, .axus-regs').forEach((n) => n.remove());
          this.paint(byRoof[rt]);
        });
        opts.appendChild(b);
      });
      box.appendChild(opts);
      box.insertAdjacentHTML(
        'beforeend',
        `<p class="axus-reg__msg">None of these match your roof? <a href="${this.mailto(`Roof check — ${plate}`)}">Send us a photo</a> and we'll tell you which bars fit.</p>`,
      );
      return box;
    }

    paint(groups) {
      const stage = this.stage;
      const anyLive = groups.some((g) => g.live);
      stage.insertAdjacentHTML(
        'beforeend',
        `<div class="axus-regs"><h3>${anyLive ? 'Roof bars that fit' : "These fit, but we're out of stock"}</h3><p>${
          anyLive ? 'Pick the set that matches your roof.' : "Leave your email on a set below and we'll tell you the day it's back."
        }</p></div>`,
      );
      groups.forEach((g, i) => {
        const box = document.createElement('section');
        box.className = `axus-regg${i === 0 && g.live ? ' is-best' : ''}${g.live ? '' : ' is-soon'}`;
        box.innerHTML = `<div class="axus-regg__head"><span class="axus-regg__sys">${esc(g.name)}</span>
            <div><strong>${esc(g.roof)}</strong><span>${esc(g.blurb || '')}</span></div>
            <span class="axus-regg__count">${esc(g.live ? (g.vehicles || []).join(' · ') : 'Restocking')}</span></div>
          <div class="axus-regg__cards"></div>`;
        const cards = box.querySelector('.axus-regg__cards');
        (g.offers || []).forEach((o) => cards.appendChild(this.offer(o, g)));
        stage.appendChild(box);
      });
    }

    offer(o, g) {
      const card = document.createElement('div');
      card.className = 'axus-rego';
      const name = `${esc(g.name)} roof bars, ${esc(o.colour || '')}${o.length ? `, ${esc(o.length)}` : ''}`;
      if (!o.listed) {
        card.classList.add('is-soon');
        card.innerHTML = `<div class="axus-rego__img axus-rego__img--empty">Not listed yet</div>
          <p class="axus-rego__name">${name}</p>
          <button type="button" class="axus-btn axus-btn--ghost" data-notify>Email me when it's back</button>`;
        card.querySelector('[data-notify]').addEventListener(
          'click',
          (event) => {
            event.currentTarget.replaceWith(this.ask(`Back in stock: ${g.name} ${o.colour || ''}`.trim(), { Product: `${g.name} ${o.colour || ''}`.trim(), Reference: o.sku }));
          },
          { once: true },
        );
        return card;
      }
      card.innerHTML = `<a class="axus-rego__link" href="${productUrl(o.handle)}">
          <span class="axus-rego__img">${o.image ? `<img src="${esc(o.image)}" alt="${name}" loading="lazy">` : ''}</span>
          <span class="axus-rego__name">${name}</span></a>
        <div class="axus-rego__foot"><span class="axus-rego__price" data-price></span><span class="axus-rego__stock" data-stock></span></div>
        <button type="button" class="axus-btn axus-btn--red" data-add disabled>Add to basket</button>`;
      productData(o.handle).then((p) => {
        const button = card.querySelector('[data-add]');
        const price = card.querySelector('[data-price]');
        const stock = card.querySelector('[data-stock]');
        const variant = p && (p.variants.find((x) => x.sku === o.sku) || (p.variants.length === 1 ? p.variants[0] : null));
        if (!variant) {
          button.outerHTML = `<a class="axus-btn axus-btn--ghost" href="${productUrl(o.handle)}">View product</a>`;
          return;
        }
        card.querySelector('.axus-rego__link').href = `${productUrl(o.handle)}?variant=${variant.id}`;
        if (p.image) {
          card.querySelector('.axus-rego__img').innerHTML = `<img src="${esc(p.image)}" alt="${name}" loading="lazy">`;
        }
        price.innerHTML = p.discount_percent
          ? `<span class="axus-p__now">${esc(variant.final_price_formatted)}</span> <s class="axus-p__was">${esc(variant.price_formatted)}</s> <span class="axus-p__off">${p.discount_percent}% off</span>`
          : `<span class="axus-p__now">${esc(variant.price_formatted)}</span>`;
        if (!variant.available) {
          stock.textContent = 'Back-order';
          stock.className = 'axus-rego__stock is-out';
          button.outerHTML = `<a class="axus-btn axus-btn--ghost" href="${productUrl(o.handle)}?variant=${variant.id}">View product</a>`;
          return;
        }
        stock.textContent = 'In stock';
        button.disabled = false;
        button.addEventListener('click', () => {
          button.disabled = true;
          button.textContent = 'Adding…';
          fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
          })
            .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
            .then(() => {
              button.textContent = 'Added ✓';
              window.location.href = `${root}cart`;
            })
            .catch(() => {
              button.disabled = false;
              button.textContent = 'Add to basket';
            });
        });
      });
      return card;
    }
  }

  if (!customElements.get('axus-reg')) customElements.define('axus-reg', AxusReg);

  /* ---------------- UK make / model / year finder ---------------- */
  const UK_ROOF = { 0: 'raised', 1: 'flush', 2: 'fix' };
  const STOP = /^(?:\(.*|\/|i{1,3}|iv|vi{0,3}|mk\d+|pre-facelift|post-facelift|facelift|sw|wagon|estate|touring|variant|avant|tourer|turnier|kombi|combi|break|sportswagon|sportwagon|grandtour|allroad|\d+|[a-z]{1,2}\d{1,3}[a-z]?)$/i;
  const baseModel = (model) => {
    const words = model.split(/\s+/);
    const out = [words[0]];
    for (let i = 1; i < words.length; i += 1) {
      if (STOP.test(words[i])) break;
      out.push(words[i]);
    }
    return out.join(' ');
  };
  let ukData;
  const loadUk = (src) => {
    ukData =
      ukData ||
      fetch(src)
        .then((r) => {
          if (!r.ok) throw new Error(`uk fitment ${r.status}`);
          return r.json();
        })
        .then((d) => {
          d.fits.forEach((f) => {
            f.base = baseModel(f[1]);
          });
          return d;
        });
    return ukData;
  };

  const productCard = (handle) => {
    const card = document.createElement('div');
    card.className = 'axus-rego';
    card.innerHTML = `<a class="axus-rego__link" href="${productUrl(handle)}"><span class="axus-rego__img"></span><span class="axus-rego__name">Loading…</span></a>
      <div class="axus-rego__foot"><span class="axus-rego__price" data-price></span><span class="axus-rego__stock" data-stock></span></div>
      <a class="axus-btn axus-btn--red" href="${productUrl(handle)}">View bars</a>`;
    productData(handle).then((p) => {
      if (!p) return card.remove();
      const v = p.variants.find((x) => x.available) || p.variants[0];
      const name = p.title
        .replace(/^AUTOAXORY\s+/i, '')
        .replace(/^.*?\b(?:19|20)\d\d(?:\s*-\s*(?:19|20)\d\d)?,?\s*/, '')
        .replace(/\.$/, '');
      card.querySelector('.axus-rego__name').textContent = name || p.title;
      if (p.image) card.querySelector('.axus-rego__img').innerHTML = `<img src="${esc(p.image)}" alt="" loading="lazy">`;
      card.querySelector('[data-price]').innerHTML = p.discount_percent
        ? `<span class="axus-p__now">${esc(v.final_price_formatted)}</span> <s class="axus-p__was">${esc(v.price_formatted)}</s>`
        : `<span class="axus-p__now">${esc(v.price_formatted)}</span>`;
      const stock = card.querySelector('[data-stock]');
      stock.textContent = p.available ? 'In stock' : 'Back-order';
      if (!p.available) stock.className = 'axus-rego__stock is-out';
    });
    return card;
  };

  class AxusUkFit extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector('form');
      this.make = this.form.elements.make;
      this.model = this.form.elements.model;
      this.year = this.form.elements.year;
      this.button = this.form.querySelector('[type="submit"]');
      this.results = this.querySelector('[data-results]');
      const warm = () => this.ready().catch(() => {});
      ['pointerdown', 'focusin', 'touchstart'].forEach((type) =>
        this.addEventListener(type, warm, { once: true, passive: true }),
      );
      if (this.closest('.axus-regfb')) warm();
      this.make.addEventListener('change', () => this.onMake());
      this.model.addEventListener('change', () => this.onModel());
      this.year.addEventListener('change', () => this.sync());
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.show();
      });
    }

    ready() {
      this.readyPromise =
        this.readyPromise ||
        loadUk(this.dataset.src)
          .then((d) => {
            this.data = d;
            this.fill(this.make, d.makes, 'Select make');
            return d;
          })
          .catch((error) => {
            this.results.hidden = false;
            this.results.innerHTML = `<p class="axus-ymm__msg">The vehicle list didn't load. Use the search at the top of the page instead.</p>`;
            throw error;
          });
      return this.readyPromise;
    }

    fill(select, values, placeholder) {
      const keep = select.value;
      select.length = 1;
      select.options[0].textContent = placeholder;
      values.forEach((v) => select.add(new Option(v, v)));
      select.disabled = values.length === 0;
      if (values.map(String).includes(keep)) select.value = keep;
    }

    rows() {
      const mi = this.data.makes.indexOf(this.make.value);
      return this.data.fits.filter((f) => f[0] === mi);
    }

    onMake() {
      const models = this.make.value ? [...new Set(this.rows().map((f) => f.base))].sort(byText) : [];
      this.model.value = '';
      this.fill(this.model, models, 'Select model');
      this.fill(this.year, [], 'Select year');
      this.sync();
    }

    onModel() {
      const rows = this.rows().filter((f) => f.base === this.model.value);
      const ys = new Set();
      rows.forEach((f) => {
        for (let y = f[2]; y <= f[3]; y += 1) ys.add(y);
      });
      this.year.value = '';
      this.fill(this.year, [...ys].sort((a, b) => b - a), 'Select year');
      this.sync();
    }

    sync() {
      this.button.disabled = !(this.make.value && this.model.value && this.year.value);
      this.results.hidden = true;
    }

    show() {
      if (!this.data) return;
      const year = Number(this.year.value);
      const rows = this.rows().filter((f) => f.base === this.model.value && f[2] <= year && year <= f[3]);
      const label = `${year} ${this.make.value} ${this.model.value}`;
      track('axus_ymm_lookup', { outcome: rows.length ? 'match' : 'no_fit', make: this.make.value });
      this.results.hidden = false;
      if (!rows.length) {
        this.results.innerHTML = `<p class="axus-ymm__msg">We don't list bars for the ${esc(label)} yet.</p>`;
        return;
      }
      const roofs = new Set(rows.map((f) => f[4]));
      this.results.innerHTML = `<p class="axus-ymm__count">Bars listed for your ${esc(label)}${
        roofs.size > 1 ? ': pick the version that matches your roof' : ''
      }</p>`;
      rows.forEach((f) => {
        const rt = UK_ROOF[f[4]];
        const box = document.createElement('section');
        box.className = 'axus-regg';
        box.innerHTML = `<div class="axus-regg__head"><span class="axus-regg__sys">${esc(ROOF[rt].t)}</span>
            <div><strong>${esc(this.make.value)} ${esc(f[1])}</strong><span>${esc(years(f[2], f[3]))} · ${esc(ROOF[rt].d)}</span></div></div>
          <div class="axus-regg__cards"></div>`;
        const cards = box.querySelector('.axus-regg__cards');
        f[5].forEach((h) => cards.appendChild(productCard(h)));
        this.results.appendChild(box);
      });
    }
  }

  if (!customElements.get('axus-ukfit')) customElements.define('axus-ukfit', AxusUkFit);

  /* ---------------- Other sets for the same car (UK product page) ---------------- */
  document.querySelectorAll('[data-axus-alts]').forEach((box) => {
    const handle = box.dataset.handle;
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
    idle(() =>
      loadUk(box.dataset.src)
        .then((d) => {
          const entry = d.fits.find((f) => f[5].includes(handle));
          const others = entry ? entry[5].filter((h) => h !== handle) : [];
          if (!others.length) return;
          box.querySelector('[data-cards]').append(...others.slice(0, 8).map(productCard));
          box.hidden = false;
        })
        .catch(() => {}),
    );
  });

  /* ---------------- Sticky add to cart (mobile product page) ---------------- */
  document.querySelectorAll('[data-axus-sticky]').forEach((bar) => {
    const target = document.getElementById(bar.dataset.target);
    if (!target || !('IntersectionObserver' in window)) return;
    const button = target.querySelector('[type="submit"][name="add"]');
    if (!button) return;
    const dock = document.querySelector('.mobile-dock');
    const place = () => {
      const h = dock && getComputedStyle(dock).display !== 'none' ? dock.getBoundingClientRect().height : 0;
      bar.style.bottom = `${h}px`;
    };
    place();
    window.addEventListener('resize', place, { passive: true });
    new IntersectionObserver(([entry]) => {
      bar.classList.toggle('is-visible', !entry.isIntersecting && !button.disabled);
    }).observe(button);
    bar.querySelector('button').addEventListener('click', () => {
      track('axus_sticky_add', {});
      button.click();
    });
  });

  /* ---------------- Random product rows ---------------- */
  document.querySelectorAll('[data-axus-random]').forEach((row) => {
    const keep = Number(row.dataset.axusRandom) || 5;
    const cards = [...row.children];
    for (let i = cards.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [cards[i], cards[j]] = [cards[j], cards[i]];
    }
    cards.forEach((card, i) => {
      if (i < keep) row.appendChild(card);
      else card.remove();
    });
  });

  /* ---------------- Tabs ---------------- */
  document.querySelectorAll('[data-axus-tabs]').forEach((list) => {
    const tabs = [...list.querySelectorAll('[role="tab"]')];
    const activate = (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        activate(tabs[(i + step + tabs.length) % tabs.length], true);
      });
    });
  });

  /* ---------------- Product gallery ---------------- */
  document.querySelectorAll('[data-axus-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('[data-track]');
    const slides = [...track.children];
    const thumbs = [...gallery.querySelectorAll('[data-thumb]')];
    const count = gallery.querySelector('[data-count]');
    const smooth = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    thumbs.forEach((thumb, i) =>
      thumb.addEventListener('click', () => track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth })),
    );

    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = slides.indexOf(entry.target);
          thumbs.forEach((t, j) => t.setAttribute('aria-current', String(j === index)));
          if (count) count.textContent = `${index + 1} / ${slides.length}`;
        }),
      { root: track, threshold: 0.6 },
    );
    slides.forEach((slide) => observer.observe(slide));
  });

  /* ---------------- Variant picker (multi-variant products) ---------------- */
  const variantsJson = document.querySelector('[data-axus-variants]');
  if (variantsJson) {
    const variants = JSON.parse(variantsJson.textContent);
    const form = document.querySelector('.axus-product-form');
    const pct = Number(form && form.dataset.axusPct) || 0;
    const groups = [...document.querySelectorAll('[data-axus-option]')];
    const priceEl = document.querySelector('[data-axus-price]');
    const stockEl = document.querySelector('[data-axus-stock]');
    const update = () => {
      const chosen = groups.map((g) => {
        const on = g.querySelector('[aria-pressed="true"]');
        return on ? on.dataset.value : null;
      });
      const v = variants.find((x) => x.options.every((o, i) => o === chosen[i]));
      const input = form && form.querySelector('[name="id"]');
      const button = form && form.querySelector('[name="add"]');
      if (!v || !input || !button) {
        if (button) button.disabled = true;
        return;
      }
      input.value = v.id;
      priceEl.innerHTML = pct
        ? `<span class="axus-p axus-p--lg"><span class="axus-p__now">${esc(v.final)}</span> <s class="axus-p__was">${esc(v.price)}</s> <span class="axus-p__off">${pct}% off</span></span>`
        : `<span class="axus-p axus-p--lg"><span class="axus-p__now">${esc(v.price)}</span></span>`;
      stockEl.textContent = v.available ? 'In stock' : 'Out of stock';
      stockEl.classList.toggle('axus-stock--out', !v.available);
      button.disabled = !v.available;
      button.querySelector('span').textContent = v.available ? 'Add to cart' : 'Sold out';
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url);
    };
    groups.forEach((g) =>
      g.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-value]');
        if (!button) return;
        g.querySelectorAll('button[data-value]').forEach((x) => x.setAttribute('aria-pressed', String(x === button)));
        const label = g.querySelector('[data-axus-option-value]');
        if (label) label.textContent = button.dataset.value;
        update();
      }),
    );
  }

  /* ---------------- Quantity ---------------- */
  document.querySelectorAll('[data-axus-qty]').forEach((qty) => {
    const input = qty.querySelector('input');
    qty.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-step]');
      if (!button) return;
      const next = Math.min(99, Math.max(1, (parseInt(input.value, 10) || 1) + Number(button.dataset.step)));
      input.value = String(next);
    });
  });

  /* ---------------- "Fits your vehicle" on product pages ---------------- */
  const fitcheck = document.querySelector('[data-axus-fitcheck]');
  const runFitcheck = (vehicle) => {
    if (!fitcheck || !vehicle) return;
    loadData(fitcheck.dataset.src)
      .then((data) => {
        const handle = fitcheck.dataset.handle;
        const row = data.fits.find((f) => f[6] === handle || f[7] === handle);
        if (!row) return;
        const name = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
        const fits =
          data.makes[row[0]] === vehicle.make &&
          row[1] === vehicle.model &&
          row[3] <= vehicle.year &&
          vehicle.year <= row[4];
        fitcheck.className = `axus-fitcheck ${fits ? 'axus-fitcheck--yes' : 'axus-fitcheck--no'}`;
        fitcheck.innerHTML = fits
          ? `✓ Listed for your ${esc(name)}. Check your rail type below before ordering.`
          : `This set isn't listed for your saved ${esc(name)}. <a href="${root}#find-your-fit">Find the right fit</a>`;
        fitcheck.hidden = false;
      })
      .catch(() => {});
  };
  runFitcheck(readVehicle());
  document.addEventListener('axus:vehicle', (event) => runFitcheck(event.detail));

  /* ---------------- Announcement rotator ---------------- */
  document.querySelectorAll('[data-axus-rotator]').forEach((bar) => {
    const slides = [...bar.querySelectorAll('.axus-announce__slide')];
    if (slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const interval = Number(bar.dataset.interval) || 5000;
    let index = 0;
    let paused = false;
    const show = (next) => {
      const current = slides[index];
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');
      current.querySelectorAll('a').forEach((a) => (a.tabIndex = -1));
      setTimeout(() => current.classList.remove('is-leaving'), 600);
      index = next;
      slides[index].classList.add('is-active');
      slides[index].removeAttribute('aria-hidden');
      slides[index].querySelectorAll('a').forEach((a) => a.removeAttribute('tabindex'));
    };
    ['mouseenter', 'focusin'].forEach((e) => bar.addEventListener(e, () => (paused = true)));
    ['mouseleave', 'focusout'].forEach((e) => bar.addEventListener(e, () => (paused = false)));
    setInterval(() => {
      if (!paused && !document.hidden) show((index + 1) % slides.length);
    }, interval);
  });

  /* ---------------- Estimated delivery dates ---------------- */
  document.querySelectorAll('[data-axus-eta]').forEach((eta) => {
    const target = eta.querySelector('[data-dates]');
    const addBusinessDays = (date, days) => {
      const d = new Date(date);
      let left = days;
      while (left > 0) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) left -= 1;
      }
      return d;
    };
    const fmt = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const today = new Date();
    const from = addBusinessDays(today, Number(eta.dataset.min) || 1);
    const to = addBusinessDays(today, Number(eta.dataset.max) || 3);
    if (target) target.textContent = `(${fmt(from)} – ${fmt(to)})`;
  });

  /* ---------------- Makes toggle (mobile) ---------------- */
  document.querySelectorAll('[data-axus-more]').forEach((button) =>
    button.addEventListener('click', () => {
      const list = document.getElementById(button.getAttribute('aria-controls'));
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      list.classList.toggle('is-open', open);
      button.textContent = open ? button.dataset.less : button.dataset.more;
    }),
  );
})();
